import { useEffect, useMemo, useState, useCallback } from 'react';
import { Form, Input, Modal, Row, Select, DatePicker, Checkbox } from 'antd';
import { useSaveParentNoteMutation } from '@/services/parent-api';
import { useGetNoteTypesQuery } from '@/services/general-api';
import moment from 'moment';
import ChildQuickSearch from '@/components/child-quick-search';

const idsNotToShowTextArea = [50194, 50195, 50196];
const idsNotToShowChildSearch = [null, 50194, 50195, 50196, 50198, 50220, 22000, 50227, 50229, 50230];
const idsNotToRequireReason = [50213,50202, 50229, 50230];

const dateNoteTypes = {
  50204: 'Date of CFTM',
  50205: 'Date of Disruption',
  50214: 'Date',
  50215: 'Date of Interview',
  50218: 'Date Moves Into Home',
  50220: 'Date of Hold',
  50229: 'Date Completed'
};

const reasonNoteTypes = {
  50213: [
    'Responded and declined to move forward',
    'Responded but not passed to child\'s team',
    'Responded & shared with child\'s team',
    'Did not respond'
  ],
  50202: [
    'Not enough training / Family not prepared',
    'Unrealistic expectations',
    'Not enough history/experience with fostering/behaviors',
    'AC did not provide reason'
  ],
  50216: [
    'Matched with another child',
    'Interviewing for multiple children & opted for a different one',
    'Family didn\'t feel like had support to care for child',
    'Family accepted foster placement',
    'Recent adoption'
  ],
  50219: [
    'Not enough training / Family not prepared',
    'Unrealistic expectations',
    'Not enough history/experience with fostering/behaviors',
    'Team didn\'t give enough info about child / learned more about child during transition'
  ],
  50205: [
    'Family not equipped to handle behaviors',
    'Family/Personal Issues (such as health concerns, death in family, etc.)',
    'Safety of family at risk',
    'Child involved with DOC'
  ],
  50220:[
    'Overdue on annual / relicensure',
    'Corrective Action Plan',
    'Uncooperative',
    'Pending disruption',
    'Active DCS assessment',
    'Revocation in progress'
  ]
};

const presentNoteTypes = {
  50214:[
    'Family',
    'Current Placement',
    'CASA/GAL',
    'Therapist',
    'FCM',
    'Child Adoption Consultant',
    'Family Adoption Consultant'
    //'Other'
  ]
};

const wrapOptions = (opt) => {
  if(!opt)
    return opt;

  var arr = [];
  for(var i in opt)
    arr.push({ label: opt[i], value: opt[i]});

  return arr;
};

const EditModal = ({ parentId, item, refetch, onClose }) => {
  const isNew = !item?.id;
  
  const [saveNote, { isLoading: saving }] = useSaveParentNoteMutation();
  const { data, isLoading } = useGetNoteTypesQuery();

  const [form] = Form.useForm();

  const initialValues = {
    childId: null, childName: '', noteTypeId: null, noteDate: null, dateLabel: null, reason: null, present: null, note: null
  };

  const [noteTypeId, setNoteTypeId] = useState();
  const [childId, setChildId] = useState();

  //Initial state for edits
  if(!isNew){
    //Datepicker expects moment
    var date = moment(item.noteDate);
    initialValues.childId = item.childId;
    initialValues.dateLabel = item.dateLabel;
    initialValues.noteDate = date;
    initialValues.reason = item.reason;
    if(typeof item.present === 'string')
    {
      var list = item.present.split(',');
      var types = presentNoteTypes[item.noteTypeId];
      if(types){
        for(var i in list)
        {
          if(!types.includes(list[i]))
            initialValues.presentOther = list[i];
        }
      }
      initialValues.present = list;
    }
    initialValues.present = item.present;
    initialValues.noteTypeId = item.noteTypeId;
    initialValues.note = item.note;
    initialValues.childName = item.childFirstName + ' ' + item.childLastName + ' ( ' + item.childCode + ' )';
  }

  const formModel = useMemo(() => {
    return {
      hideText: idsNotToShowTextArea.includes(noteTypeId),
      hideSearch: idsNotToShowChildSearch.includes(noteTypeId),
      reasonOptions: wrapOptions(reasonNoteTypes[noteTypeId]),
      presentOptions: wrapOptions(presentNoteTypes[noteTypeId]),
      dateLabel: dateNoteTypes[noteTypeId],
      isReasonRequired: !idsNotToRequireReason.includes(noteTypeId)
    };
  }, [noteTypeId]);


  const handleChildSelected = useCallback((childId, name) => {
    form.setFieldValue('childId', childId);
    form.setFieldValue('childName', name);
    form.validateFields(['childName']);
    setChildId(childId);
  }, [form]);


  
  useEffect(() => {
    if (item) {
      setTimeout(() => {
        form.resetFields();
        const typeId = form.getFieldValue('noteTypeId');
        const cid = form.getFieldValue('childId');
        setNoteTypeId(typeId);
        setChildId(cid);
      });
    }
  }, [form, item]);


  const noteTypes = useMemo(() => {
    if (!data) return [];
    const values = [...data]
      .sort((a, b) => {
        const numA = parseInt(a.code.replace(/\D/g, ''), 10);
        const numB = parseInt(b.code.replace(/\D/g, ''), 10);
        return numA - numB;
      })
      .map((nt) => ({ label: nt.name, value: nt.id, code: nt.code }));
    return values;
  }, [data]);


  const onFinish = useCallback(async (body) => {
      var present = form.getFieldValue('present') || [];
      var presentOther = form.getFieldValue('presentOther');

      if (presentOther)
        present.push(presentOther);

      console.log('is array: ' + Array.isArray(present));
      console.log('present: ' + present);
      var pval = '';

      if(Array.isArray(present) === true) {
        pval = present.join(',');
      } else {
        pval = present;
      }

      await saveNote({ parentId, id: item?.id, body: {...body, dateLabel: formModel.dateLabel, present: pval} }).unwrap();

      refetch();
      onClose();

    try {
      
    } catch (err) {}
  }, [parentId, item, form, formModel, onClose, refetch, saveNote]); 


  return (
    <>
      <Modal
        title={isNew ? 'Add Note' : 'Edit Note'}
        open={!!item}
        okText={isNew ? 'Add' : 'Save'}
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={onClose}
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFinish={onFinish}
          //onValuesChange={updateFields}
        >
          <Row>
            <Form.Item name="noteTypeId" label="Type" rules={[{ required: true }]} style={{ width: '100%' }}>
              <Select options={noteTypes} loading={isLoading} onChange={(val) => setNoteTypeId(val)} />
            </Form.Item>
          </Row>
          {!formModel.hideSearch && (
            <Row>
              <Form.Item name="childId" hidden={true}>
                <Input />
              </Form.Item>
              <Form.Item name="childName" label="Child" style={{ width: '100%' }} rules={[{ required: true, validator: (_, value) => 
                (childId > 0) ? Promise.resolve() : Promise.reject('Select a Child')
              }]}>
                <ChildQuickSearch onSelect={handleChildSelected} text={initialValues.childName} childId={childId}/>
              </Form.Item>
            </Row>
          )}
          {formModel.dateLabel && (
            <Row>
              <Form.Item name="dateLabel" hidden={true}>
                <Input value={formModel.dateLabel} />
              </Form.Item>
              <Form.Item name="noteDate" label={formModel.dateLabel} rules={[{ required: true }]}>
                <DatePicker
                  format="MM/DD/YYYY"
                  //value={noteDate}
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                    if (validDate.isValid()) {
                      form.setFieldsValue({
                        noteDate: validDate,
                      });
                      // setDate(dateToString(validDate));
                    } else {
                      form.setFieldsValue({
                        noteDate: null,
                      });
                    }
                  }}
                // onChange={(day) => {
                //   setDate(dateToString(day));
                // }}
                />
              </Form.Item>
            </Row>
          )}
          {formModel.reasonOptions && (
            <Row>
              <Form.Item name="reason" label="Reason" rules={[{ required: formModel.isReasonRequired }]} style={{ width: '100%' }}>
                <Select options={formModel.reasonOptions} />
              </Form.Item>
            </Row>
          )}
          {formModel.presentOptions && (
            <Row>
              <Form.Item name="present" label="Present" style={{ width: '100%' }}>
                <Checkbox.Group options={formModel.presentOptions} />
              </Form.Item>
              <Form.Item name="presentOther" style={{ width: '100%' }}>
                <Input placeholder="Other"/>
              </Form.Item>
            </Row>
          )}
          {!formModel.hideText && (
            <Row>
              <Form.Item name="note" label="Note" style={{ width: '100%' }}>
                <Input.TextArea autoSize={{ minRows: 3 }} />
              </Form.Item>
            </Row>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default EditModal;
